// frontend/src/pages/PayrollDrafts/components/FilterMenu.tsx
// Filter dropdown with "Sort by" + "Pay period" sections.
// Self-contained: owns open state, outside-click, and ESC-to-close.

import React, { useState, useEffect, useRef } from "react";
import { Filter, ChevronDown, Check } from "lucide-react";
import type { SortType, PeriodType } from "../types";
import { C, outlineBtnStyle, filterOptionStyle } from "../styles";

interface FilterMenuProps {
  sort: SortType;
  onSortChange: (sort: SortType) => void;
  period: PeriodType;
  onPeriodChange: (period: PeriodType) => void;
}

const SORT_OPTIONS: ReadonlyArray<[SortType, string]> = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["net-high", "Net total, highest first"],
  ["net-low", "Net total, lowest first"],
];

const PERIOD_OPTIONS: ReadonlyArray<[PeriodType, string]> = [
  ["all", "All periods"],
  ["this-year", "This year"],
  ["last-quarter", "Last 3 months"],
  ["this-month", "This month"],
];

export default function FilterMenu({
  sort,
  onSortChange,
  period,
  onPeriodChange,
}: FilterMenuProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(function () {
    if (!isOpen) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return function () {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setIsOpen(!isOpen)} style={outlineBtnStyle}>
        <Filter size={12} /> Filter <ChevronDown size={10} />
      </button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: 40,
            right: 0,
            background: C.card,
            border: "1px solid " + C.line,
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: "10px 0",
            minWidth: 220,
            zIndex: 100,
          }}
        >
          <SectionHeading>Sort by</SectionHeading>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt[0]}
              onClick={() => onSortChange(opt[0])}
              style={filterOptionStyle}
            >
              <span>{opt[1]}</span>
              {sort === opt[0] && <Check size={14} style={{ color: C.brand }} strokeWidth={3} />}
            </button>
          ))}

          <div style={{ height: 1, background: C.line, margin: "6px 0" }} />

          <SectionHeading>Pay period</SectionHeading>
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt[0]}
              onClick={() => onPeriodChange(opt[0])}
              style={filterOptionStyle}
            >
              <span>{opt[1]}</span>
              {period === opt[0] && <Check size={14} style={{ color: C.brand }} strokeWidth={3} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div
      style={{
        padding: "4px 14px 8px",
        fontSize: 10,
        fontWeight: 700,
        color: C.ink,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}